'use es6';

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { Redirect, Route, Switch } from 'react-router-dom';
import * as CategorySlug from 'marketplace-ui-apps-core/common/constants/urlSlugs/CategorySlug';
import * as SuperCategorySlug from 'marketplace-ui-apps-core/common/constants/urlSlugs/SuperCategorySlug';
import { CategoryV2Slug } from 'marketplace-ui-apps-core/common/constants/urlSlugs/CategoryV2Slug';
import { isFulfilled } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { usePublishedCollections } from 'marketplace-ui-common/collections/hooks/usePublishedCollections';
import DocumentHead from 'marketplace-ui-common/components/portals/DocumentHead';
import PrerenderReady from 'marketplace-ui-common/components/prerender/PrerenderReady';
import { AppDetailsPage } from '../details/AppDetailsPage';
import { PublicOnly, getIsUngatedForRecategorization } from '../selectors/auth';
import { useAppContext } from '../context/AppContext.tsx';
import AllAppCollectionsContainer from '../storefront/containers/AllAppCollectionsContainer';
import AllReviewsPage from '../containers/detail/AllReviewsPage';
import DetailContainer from '../containers/detail/DetailContainer';
import NotFoundError from './error/NotFoundError';
import SearchResults from '../containers/storefront/SearchResults';
import Storefront from '../containers/Storefront';
import StorefrontCollectionContainer from '../containers/storefront/StorefrontCollectionContainer';
import withCategoryParamValidation from '../decorators/withCategoryParamValidation';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const pageRegExp = '[1-9][0-9]*';
const getPaginatedRoutes = (route, pageRoute) => [`${pageRoute != null ? pageRoute : route}/page/:page(${pageRegExp})`, route];
export const allCategoriesRoutes = getPaginatedRoutes('/', '');
const superCategorySlugRegExp = Object.values(SuperCategorySlug).join('|');
const categorySlugRegExp = Object.values(CategorySlug).join('|');
const superCategoryRouteBase = `/:superCategory(${superCategorySlugRegExp})`;
const categoryRouteBase = `${superCategoryRouteBase}/:category(${categorySlugRegExp})`;
export const categoryRoutes = getPaginatedRoutes(`${categoryRouteBase}?`);
const categoryV2SlugRegExp = Object.values(CategoryV2Slug).join('|');
export const categoryV2Routes = getPaginatedRoutes(`/:categoryV2(${categoryV2SlugRegExp})`);
export const detailPageRoutes = ['/_preview/:offeringId', '/:slug/review', '/:slug/write-review', '/:slug'];
const allReviewsPageRoutes = ['/:slug/reviews'];
export const CategorySearchResults = withCategoryParamValidation(SearchResults);
export const DetailRouteContainer = withCategoryParamValidation(props => {
  const {
    match: {
      params: {
        slug
      }
    }
  } = props;
  return /*#__PURE__*/_jsx(DetailContainer, Object.assign({}, props), slug);
});
const NotFoundRoute = () => /*#__PURE__*/_jsxs(NavMarker, {
  name: "NOT_FOUND_ERROR_LOAD",
  children: [/*#__PURE__*/_jsx(NotFoundError, {}), /*#__PURE__*/_jsx(PublicOnly, {
    children: /*#__PURE__*/_jsx(DocumentHead, {
      children: /*#__PURE__*/_jsx(PrerenderReady, {
        statusCode: 404
      })
    })
  })]
});
export default function Routes() {
  const isUngatedForRecategorization = useSelector(getIsUngatedForRecategorization);
  const appContext = useAppContext();
  const {
    getCollectionSlugs,
    getCollectionBySlug,
    requestState: collectionsRequestState
  } = usePublishedCollections({
    appContext,
    offeringType: 'APP'
  });
  const collectionSlugs = getCollectionSlugs();
  const collectionRoutes = useMemo(() => getPaginatedRoutes(`/:collection(${collectionSlugs.join('|')})`), [collectionSlugs]);
  if (!isFulfilled(collectionsRequestState)) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Switch, {
    children: [isUngatedForRecategorization && /*#__PURE__*/_jsx(Redirect, {
      sensitive: true,
      from: superCategoryRouteBase,
      to: "/"
    }), /*#__PURE__*/_jsx(Route, {
      exact: true,
      sensitive: true,
      path: ['/featured', ...allCategoriesRoutes, ...collectionRoutes, ...(isUngatedForRecategorization ? categoryV2Routes : categoryRoutes)],
      render: props => /*#__PURE__*/_jsx(Storefront, Object.assign({}, props, {
        children: /*#__PURE__*/_jsxs(Switch, {
          children: [/*#__PURE__*/_jsx(Route, {
            exact: true,
            sensitive: true,
            path: "/featured",
            component: AllAppCollectionsContainer
          }), /*#__PURE__*/_jsx(Route, {
            exact: true,
            sensitive: true,
            path: allCategoriesRoutes,
            component: SearchResults
          }), /*#__PURE__*/_jsx(Route, {
            exact: true,
            sensitive: true,
            path: collectionRoutes,
            render: routeProps => /*#__PURE__*/_jsx(StorefrontCollectionContainer, Object.assign({}, routeProps, {
              getCollectionBySlug: getCollectionBySlug
            }))
          }), isUngatedForRecategorization ? /*#__PURE__*/_jsx(Route, {
            exact: true,
            sensitive: true,
            path: categoryV2Routes,
            component: SearchResults
          }) : /*#__PURE__*/_jsx(Route, {
            exact: true,
            sensitive: true,
            path: categoryRoutes,
            component: CategorySearchResults
          })]
        })
      }))
    }), /*#__PURE__*/_jsx(Redirect, {
      exact: true,
      sensitive: true,
      from: `${categoryRouteBase}/:slug/reviews`,
      to: `/:slug/reviews`
    }), /*#__PURE__*/_jsx(Redirect, {
      exact: true,
      sensitive: true,
      from: `${categoryRouteBase}/:slug/review`,
      to: `/:slug/write-review`
    }), /*#__PURE__*/_jsx(Redirect, {
      sensitive: true,
      from: `${categoryRouteBase}/:slug/write-review`,
      to: "/:slug/write-review"
    }), /*#__PURE__*/_jsx(Redirect, {
      sensitive: true,
      from: `${categoryRouteBase}/:slug`,
      to: "/:slug"
    }), /*#__PURE__*/_jsx(Redirect, {
      sensitive: true,
      from: "/_detail/:slug/reviews",
      to: "/:slug/reviews"
    }), /*#__PURE__*/_jsx(Redirect, {
      exact: true,
      sensitive: true,
      from: "/_detail/:slug/review",
      to: "/:slug/write-review"
    }), /*#__PURE__*/_jsx(Redirect, {
      sensitive: true,
      from: "/_detail/:slug/write-review",
      to: "/:slug/write-review"
    }), /*#__PURE__*/_jsx(Redirect, {
      exact: true,
      sensitive: true,
      from: "/_detail/:slug",
      to: "/:slug"
    }), /*#__PURE__*/_jsx(Route, {
      exact: true,
      sensitive: true,
      path: [...detailPageRoutes, ...allReviewsPageRoutes],
      render: props => /*#__PURE__*/_jsx(DetailRouteContainer, Object.assign({}, props, {
        children: /*#__PURE__*/_jsxs(Switch, {
          children: [/*#__PURE__*/_jsx(Redirect, {
            exact: true,
            sensitive: true,
            from: "/:slug/review",
            to: "/:slug/write-review"
          }), /*#__PURE__*/_jsx(Route, {
            path: allReviewsPageRoutes,
            component: AllReviewsPage
          }), /*#__PURE__*/_jsx(Route, {
            path: detailPageRoutes,
            component: AppDetailsPage
          })]
        })
      }))
    }), /*#__PURE__*/_jsx(Route, {
      component: NotFoundRoute
    })]
  });
}